import React, { useEffect, useState } from 'react'

interface IUseWrapButtonFocus {
  keyboardShortcuts: (keyboardEvent: React.KeyboardEvent) => void
  forceFocusElement: (itemId: string) => void
}

export const useWrapButtonFocus = (
  wrapComponent: React.MutableRefObject<HTMLDivElement | null>
): IUseWrapButtonFocus => {
  //
  const [wrappedButtonsElements, setWrappedButtonsElements] = useState<HTMLButtonElement[]>([])

  useEffect(() => {
    if (wrapComponent.current) {
      const allButtonsOfWrapper = Array.from(wrapComponent.current.querySelectorAll('button'))
      setWrappedButtonsElements(allButtonsOfWrapper)
    }
  }, [wrapComponent])

  const handleFocusElement = (buttonId: string): void => {
    if (wrappedButtonsElements) {
      wrappedButtonsElements.forEach(button => {
        if (button.id === buttonId) {
          button.setAttribute('tabindex', '0')
          button.focus()
          return
        }
        button.setAttribute('tabindex', '-1')
        button.blur()
      })
    }
  }

  const forceFocusElement = (itemId: string): void => {
    handleFocusElement(itemId)
  }

  const selectLastTab = (): void => {
    if (wrappedButtonsElements) {
      const lastTab = wrappedButtonsElements[wrappedButtonsElements.length - 1]
      handleFocusElement(lastTab.id)
    }
  }

  const selectFirstTab = (): void => {
    if (wrappedButtonsElements) {
      const firstTab = wrappedButtonsElements[0]
      handleFocusElement(firstTab.id)
    }
  }

  const selectNextTab = (): void => {
    if (wrappedButtonsElements) {
      const nextTab = wrappedButtonsElements.find(
        (tab, index) => wrappedButtonsElements[index - 1]?.getAttribute('tabindex') === '0'
      )

      if (nextTab) {
        handleFocusElement(nextTab.id)
      } else {
        const firstTab = wrappedButtonsElements[0]
        handleFocusElement(firstTab.id)
      }
    }
  }

  const selectPreviousTab = (): void => {
    if (wrappedButtonsElements) {
      const previousTab = wrappedButtonsElements.find(
        (tab, index) => wrappedButtonsElements[index + 1]?.getAttribute('tabindex') === '0'
      )

      if (previousTab) {
        handleFocusElement(previousTab.id)
      } else {
        const lastTab = wrappedButtonsElements[wrappedButtonsElements.length - 1]
        handleFocusElement(lastTab.id)
      }
    }
  }

  const keyboardShortcuts = (event: React.KeyboardEvent): void => {
    const { key } = event

    switch (key) {
      case 'End':
        event.preventDefault()
        return selectLastTab()
      case 'Home':
        event.preventDefault()
        return selectFirstTab()
      case 'ArrowRight':
        event.preventDefault()
        return selectNextTab()
      case 'ArrowLeft':
        event.preventDefault()
        return selectPreviousTab()
    }
  }

  return { keyboardShortcuts, forceFocusElement }
}
